import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../config";
import Backdrop from "../../Catalogs/Dialog/Backdrop";
import Spin from "../../Spinner/Spinner";
import styles from "./CreateCartDialog.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { TfiReload } from "react-icons/tfi";


export const CreateCartDialog = ({
  show = false,
  onClose = () => {},
  userId,
  onCreated = () => {},
}) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [cartProducts, setCartProducts] = useState([]);

  const {id} = useParams()

  const createCart = async () => {
    setLoadingProducts(true);
    try {
       const response = await axios.post(`${API}/Admin/carts`, {
        userId: id,
        products: cartProducts.map((p) => ({
          productId: p._id,
          quantity: p.quantity || 1,
        })),
       })
       if (response.status === 201 || response.status === 200) {
        toast.success("Cart created successfully");
        onCreated();
        onClose();
      } else {
        toast.error("Failed to create cart");
      }
    } catch (error) {
      console.error("Failed to create cart:", error);
      toast.error("Failed to create cart");
    } finally {
      setLoadingProducts(false);
    }
  }

  useEffect(() => {
    if (!show) {
      setSearch("");
      setDebouncedSearch("");
      setProducts([]);
      setCartProducts([]);
    }
  }, [show]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!show) return;
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await axios.get(`${API}/Admin/addProduct`, {
          params: {
            search: debouncedSearch || undefined,
            limit: 5,
            page: 1,
          },
        });
        setProducts(response.data.products || []);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        toast.error("Failed to fetch products");
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [debouncedSearch, show]);

  const availableProducts = useMemo(() => {
    const selectedIds = new Set(cartProducts.map((p) => p._id));
    return (products || []).filter((p) => !selectedIds.has(p._id));
  }, [products, cartProducts]);

  const handleAddToCart = (product) => {
    setCartProducts((prev) => {
      const existing = prev.find((p) => p._id === product._id);
      if (existing) {
        return prev.map((p) =>
          p._id === product._id ? { ...p, quantity: (p.quantity || 0) + 1 } : p
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleQuantityChange = (productId, value) => {
    const quantity = Math.max(1, Number(value) || 1);
    setCartProducts((prev) =>
      prev.map((p) =>
        p._id === productId
          ? {
              ...p,
              quantity,
            }
          : p
      )
    );
  };

  const handleRemoveFromCart = (productId) => {
    setCartProducts((prev) => prev.filter((p) => p._id !== productId));
  };

  const handleCreateCart = async () => {
    if (!userId) {
      toast.error("User not found");
      return;
    }
    if (!cartProducts.length) {
      toast.error("Add at least one product to create a cart");
      return;
    }
    try {
      createCart();
    } catch (error) {
      console.error("Failed to create cart:", error);
      toast.error("Failed to create cart");
    } finally {
      setLoadingProducts(false);
    }
  };

  if (!show) return null;

  return (
    <div>
      <Backdrop show={show} switch={onClose} />
      <div className={styles.popup}>
        <div className={styles.header}>
          <h2 className={styles.title}>Create Cart</h2>
          <button className={styles.generateKitButton}  onClick={() => navigate(`/generate-kit?userId=${id}&newCart=true`)} > <TfiReload/> Generate Kit</button>
        </div>

        <div className={styles.content}>
          <div className={styles.columnsHeader}>
            <div className={styles.columnHeader}>Products</div>
            <div className={styles.columnHeader}>Cart</div>
          </div>
          <div className={styles.columns}>
            <div className={styles.column}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className={styles.listContainer}>
                {loadingProducts ? (
                  <div className={styles.spinnerWrapper}>
                    <Spin />
                  </div>
                ) : availableProducts.length ? (
                  availableProducts.map((product) => (
                    <button
                      key={product._id}
                      type="button"
                      className={styles.productRow}
                      onClick={() => handleAddToCart(product)}
                    >
                      <div className={styles.productMain}>
                        <div className={styles.productImageWrapper}>
                          {product.image?.[0] ? (
                            <img
                              src={product.image[0]}
                              alt={product.name}
                              className={styles.productImage}
                            />
                          ) : (
                            <div className={styles.productImagePlaceholder} />
                          )}
                        </div>
                        <div className={styles.productInfo}>
                          <div className={styles.productName}>
                            {product.name || "-"}
                          </div>
                          <div className={styles.productMeta}>
                            {product.brandName || "-"}
                          </div>
                        </div>
                      </div>
                      <div className={styles.productPrice}>
                        ₹{Number((product.price[0].cost ?? 0) *(1+product.price[0].margin/100)).toFixed(0)}
                      
                      
                      </div>
                    </button>
                  ))
                ) : (
                  <div className={styles.emptyState}>No products found</div>
                )}
              </div>
            </div>

            <div className={styles.column}>
              <div className={styles.listContainer}>
                {cartProducts.length ? (
                  cartProducts.map((product) => (
                    <div key={product._id} className={styles.cartRow}>
                      <div className={styles.cartInfo}>
                        <div className={styles.productName}>
                          {product.name || "-"}
                        </div>
                        <div className={styles.productMeta}>
                          {product.brandName || "-"}
                        </div>
                      </div>
                      <div className={styles.cartControls}>
                        <label className={styles.qtyLabel}>Qty</label>
                        <input
                          type="number"
                          min="1"
                          className={styles.qtyInput}
                          value={product.quantity || 1}
                          onChange={(e) =>
                            handleQuantityChange(product._id, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => handleRemoveFromCart(product._id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    No products in cart yet
                  </div>
                )}
              </div>
              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.confirmButton}
                  onClick={handleCreateCart}
                >
                  Create Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

